<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { Receipt, Tag, Loader2 } from 'lucide-vue-next'
import axios from 'axios'
import { timeStringToAdjustedMinutes, LATE_NIGHT_START_HOUR } from '@/composables/useNightPlanEvents'

interface BreakdownItem {
  plan_name: string
  plan_type: string
  duration_minutes: number
  base_amount: number
  extra_guests: number
  extra_guest_amount: number
  subtotal: number
}

interface PricingResult {
  breakdown: BreakdownItem[]
  subtotal: number
  discount: number
  total: number
  coupon_applied: boolean
}

const props = withDefaults(defineProps<{
  reservationDate: string
  // 未選択時は data 属性ごと出力されないため optional（空文字を渡すと {} に化ける）
  startTime?: string
  endTime?: string
  numberOfGuests: number
  changePath?: string
  // バックエンドの ReservationService.slot_min_hour（開店時刻の「時」）。
  // 深夜帯（この値未満 = 営業日の翌日側）の時刻を +24h 正規化するために必要。
  slotMinHour?: number
}>(), {
  startTime: '',
  endTime: '',
  changePath: undefined,
  slotMinHour: 10
})

const currentDate = ref(props.reservationDate)
const currentStartTime = ref(props.startTime)
const currentEndTime = ref(props.endTime)
const currentGuests = ref(props.numberOfGuests)

const couponCode = ref('')
const couponInput = ref('')
const couponError = ref('')
const couponApplied = ref(false)
const loading = ref(false)
const pricing = ref<PricingResult | null>(null)
const error = ref('')
const difference = ref<{ kind: string; amount: number } | null>(null)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let endTimeObserver: MutationObserver | null = null
// fetchPricing の連続呼び出し（debounce未経由のapplyCoupon/onMounted含む）で、遅延した
// 古いレスポンスが pricing/difference を上書きしないよう、最新リクエストのみ反映する逐次トークン。
let fetchSeq = 0

const canCalculate = computed(() => {
  return currentDate.value && currentStartTime.value && currentEndTime.value && currentGuests.value > 0
})

// 変更後の時間帯がナイト帯（20:00以降、または深夜またぎ）に掛かるか。
// 差額注記（ナイト固定料金加算）をこの場合のみ表示するために使う。
//
// 時刻は必ず slotMinHour 基準で +24h 正規化してから比較する。
// 素の hour*60+min は「深夜0時を法とする巡回量」なので、01:00-02:00 のような
// 営業日の翌日側に収まる予約を「ナイト帯でない」と誤判定し、
// バックエンドはナイト固定料金で請求しているのに注記が出ない、という食い違いが起きる（issue #331）。
const involvesNight = computed(() => {
  const s = currentStartTime.value, e = currentEndTime.value
  if (!s || !e) return false

  const nightStartMin = LATE_NIGHT_START_HOUR * 60
  const startMin = timeStringToAdjustedMinutes(s, props.slotMinHour)
  const endMin = timeStringToAdjustedMinutes(e, props.slotMinHour)

  return startMin >= nightStartMin || endMin > nightStartMin
})

const csrfToken = computed(() => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
})

function observeFormChanges() {
  const form = document.querySelector('form[data-controller="reservation-form"]') as HTMLFormElement | null
  if (!form) return

  form.addEventListener('change', handleFormChange)
  form.addEventListener('input', handleFormInput)

  // end_time_frameの中身がStimulusで動的に差し替えられるのを監視
  const endTimeFrame = document.getElementById('end_time_frame')
  if (endTimeFrame) {
    endTimeObserver = new MutationObserver(() => {
      updateFromForm()
      debounceFetch()
    })
    endTimeObserver.observe(endTimeFrame, { childList: true, subtree: true })
  }
}

function handleFormChange() {
  updateFromForm()
  debounceFetch()
}

function handleFormInput(e: Event) {
  const target = e.target as HTMLInputElement
  if (target.name === 'reservation[number_of_guests]') {
    updateFromForm()
    debounceFetch()
  }
}

function updateFromForm() {
  const dateField = document.querySelector<HTMLInputElement>('[name="reservation[reservation_date]"]')
  const startField = document.querySelector<HTMLSelectElement>('[name="reservation[start_time]"]')
  const endField = document.querySelector<HTMLSelectElement | HTMLInputElement>('[name="reservation[end_time]"]')
  const guestsField = document.querySelector<HTMLInputElement>('[name="reservation[number_of_guests]"]')

  if (dateField) currentDate.value = dateField.value
  if (startField) currentStartTime.value = startField.value
  if (endField) currentEndTime.value = endField.value
  if (guestsField) currentGuests.value = parseInt(guestsField.value) || 1
}

function debounceFetch() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => fetchPricing(), 300)
}

async function fetchPricing() {
  if (!canCalculate.value) {
    pricing.value = null
    return
  }

  const seq = ++fetchSeq
  loading.value = true
  error.value = ''
  couponError.value = ''

  try {
    const response = await axios.post('/api/v1/pricing/calculate', {
      reservation_date: currentDate.value,
      start_time: currentStartTime.value,
      end_time: currentEndTime.value,
      number_of_guests: currentGuests.value,
      coupon_code: couponCode.value || undefined,
    }, {
      headers: {
        'X-CSRF-Token': csrfToken.value,
      },
    })

    // 後発のfetchPricingが既に走っていれば、この古いレスポンスは破棄する（stale上書き防止）。
    if (seq !== fetchSeq) return

    pricing.value = response.data
    couponApplied.value = response.data.coupon_applied
    await fetchDifference(seq)
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data?.error) {
      const errMsg = e.response.data.error as string
      if (errMsg.includes('クーポン')) {
        couponError.value = errMsg
        couponCode.value = ''
        couponApplied.value = false
        await fetchPricingWithoutCoupon()
      } else if (errMsg.includes('必須パラメータ') || errMsg.includes('料金プランが見つかりません')) {
        // 入力途中のパラメータ不足は表示しない
        pricing.value = null
      } else {
        error.value = errMsg
      }
    } else {
      error.value = '料金の取得に失敗しました'
    }
  } finally {
    loading.value = false
  }
}

async function fetchPricingWithoutCoupon() {
  try {
    const response = await axios.post('/api/v1/pricing/calculate', {
      reservation_date: currentDate.value,
      start_time: currentStartTime.value,
      end_time: currentEndTime.value,
      number_of_guests: currentGuests.value,
    }, {
      headers: {
        'X-CSRF-Token': csrfToken.value,
      },
    })
    pricing.value = response.data
  } catch {
    // ignore
  }
}

async function fetchDifference(seq: number = fetchSeq) {
  if (!props.changePath) return
  try {
    const res = await axios.post(props.changePath, {
      reservation: {
        start_time: currentStartTime.value,
        end_time: currentEndTime.value,
        number_of_guests: currentGuests.value,
      },
    }, { headers: { 'X-CSRF-Token': csrfToken.value } })
    // 後発のfetchPricingが走っていれば古いレスポンスは破棄（stale上書き防止）。
    if (seq !== fetchSeq) return
    // サーバが返す diff_cents（全帯=日中＋ナイト固定額を算入し、残額cap・クーポン比例を反映した確定値）をそのまま使う。
    difference.value = { kind: res.data.kind, amount: Math.abs(res.data.diff_cents) }
  } catch {
    difference.value = null
  }
}

function syncCouponHiddenField() {
  const hidden = document.getElementById('coupon_code_hidden') as HTMLInputElement | null
  if (hidden) hidden.value = couponCode.value
}

function applyCoupon() {
  if (!couponInput.value.trim()) return
  couponCode.value = couponInput.value.trim().toUpperCase()
  syncCouponHiddenField()
  fetchPricing()
}

function removeCoupon() {
  couponCode.value = ''
  couponInput.value = ''
  couponError.value = ''
  couponApplied.value = false
  syncCouponHiddenField()
  fetchPricing()
}

function formatPrice(cents: number): string {
  return cents.toLocaleString('ja-JP')
}

onMounted(() => {
  observeFormChanges()
  if (canCalculate.value) {
    fetchPricing()
  }
})

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (endTimeObserver) endTimeObserver.disconnect()
  const form = document.querySelector('form[data-controller="reservation-form"]') as HTMLFormElement | null
  if (form) {
    form.removeEventListener('change', handleFormChange)
    form.removeEventListener('input', handleFormInput)
  }
})
</script>

<template>
  <div class="mt-6">
    <!-- クーポン入力 -->
    <div class="mb-4">
      <label class="block text-sm font-bold text-[#5a3a26] mb-1.5">
        <Tag
          class="inline w-4 h-4 mr-1"
          :stroke-width="2.5"
        />
        クーポンコード（任意）
      </label>
      <div
        v-if="couponApplied"
        class="flex items-center gap-2"
      >
        <span class="inline-flex items-center gap-1 px-3 py-1.5 bg-lime-100 border-2 border-lime-400 text-lime-700 font-bold text-sm">
          <Tag
            class="w-3.5 h-3.5"
            :stroke-width="2.5"
          />
          {{ couponCode }}
        </span>
        <button
          type="button"
          class="text-sm text-red-500 font-bold hover:text-red-700"
          @click="removeCoupon"
        >
          解除
        </button>
      </div>
      <div
        v-else
        class="flex gap-2"
      >
        <input
          v-model="couponInput"
          type="text"
          placeholder="コードを入力"
          class="flex-1 min-w-0 retro-input text-sm"
          @keyup.enter="applyCoupon"
        >
        <button
          type="button"
          class="retro-btn retro-btn-secondary text-xs px-4"
          :disabled="!couponInput.trim()"
          @click="applyCoupon"
        >
          適用
        </button>
      </div>
      <p
        v-if="couponError"
        class="mt-1 text-sm text-red-600 font-bold"
      >
        {{ couponError }}
      </p>
    </div>

    <!-- 料金表示 -->
    <div class="border-4 border-[#5a3a26] bg-white p-4">
      <div class="flex items-center gap-2 mb-3">
        <Receipt
          class="w-5 h-5 text-[#5a3a26]"
          :stroke-width="2.5"
        />
        <h3 class="font-bold text-[#5a3a26]">
          料金内訳
        </h3>
      </div>

      <!-- ローディング（料金未取得の場合のみ全面表示） -->
      <div
        v-if="loading && !pricing"
        class="flex items-center justify-center py-4 text-[#8b5a3b]"
      >
        <Loader2 class="w-5 h-5 animate-spin mr-2" />
        <span class="text-sm font-bold">計算中...</span>
      </div>

      <!-- エラー -->
      <div
        v-else-if="error"
        class="py-2 text-sm text-red-600 font-bold"
      >
        {{ error }}
      </div>

      <!-- 選択待ち -->
      <div
        v-else-if="!pricing"
        class="py-2 text-sm text-[#8b5a3b]"
      >
        日時と人数を選択すると料金が表示されます
      </div>

      <!-- 料金内訳 -->
      <div v-else>
        <div
          v-for="(item, index) in pricing.breakdown"
          :key="index"
          class="mb-2 pb-2 border-b border-[#5a3a26]/10 last:border-0"
        >
          <div class="flex justify-between text-sm">
            <span class="text-[#5a3a26] font-bold">{{ item.plan_name }}</span>
            <span class="text-[#5a3a26]">¥{{ formatPrice(item.base_amount) }}</span>
          </div>
          <div class="text-xs text-[#8b5a3b] ml-2">
            {{ item.duration_minutes >= 60 ? Math.floor(item.duration_minutes / 60) + '時間' : '' }}{{ item.duration_minutes % 60 > 0 ? (item.duration_minutes % 60) + '分' : '' }}
          </div>
          <div
            v-if="item.extra_guests > 0"
            class="flex justify-between text-sm mt-1"
          >
            <span class="text-[#8b5a3b] ml-2">追加{{ item.extra_guests }}名</span>
            <span class="text-[#5a3a26]">¥{{ formatPrice(item.extra_guest_amount) }}</span>
          </div>
        </div>

        <!-- 小計 -->
        <div
          v-if="pricing.discount > 0"
          class="flex justify-between text-sm mt-2"
        >
          <span class="text-[#8b5a3b]">小計</span>
          <span class="text-[#5a3a26]">¥{{ formatPrice(pricing.subtotal) }}</span>
        </div>

        <!-- 割引 -->
        <div
          v-if="pricing.discount > 0"
          class="flex justify-between text-sm text-lime-600 font-bold"
        >
          <span>クーポン割引</span>
          <span>-¥{{ formatPrice(pricing.discount) }}</span>
        </div>

        <!-- 合計 -->
        <div class="flex justify-between items-center mt-3 pt-3 border-t-2 border-[#5a3a26]">
          <span class="font-black text-[#5a3a26]">合計</span>
          <span class="text-xl font-black text-[#5a3a26]">
            <Loader2
              v-if="loading"
              class="w-5 h-5 animate-spin inline mr-1"
            />
            ¥{{ formatPrice(pricing.total) }}
          </span>
        </div>

        <!-- 変更差額（追加 / 返金）。残額capで0になった返金は :none かつ amount 0 のため表示しない。 -->
        <div
          v-if="difference && difference.kind !== 'none' && difference.amount > 0"
          class="mt-3 pt-3 border-t-2 border-dashed border-[#5a3a26]/40"
        >
          <div class="flex justify-between items-center">
            <span class="font-bold text-[#5a3a26]">
              {{ difference.kind === 'charge' ? '追加料金' : '返金' }}
            </span>
            <span
              class="text-lg font-black"
              :class="difference.kind === 'charge' ? 'text-red-600' : 'text-lime-600'"
            >
              {{ difference.kind === 'charge' ? '+' : '-' }}¥{{ formatPrice(difference.amount) }}
            </span>
          </div>
          <p
            v-if="involvesNight"
            class="text-xs text-[#8b5a3b] mt-1"
          >
            ※ ナイト帯（20:00以降）に跨る変更は固定料金が加算されます
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
